import { Button, Card, PageHeading } from "@peer-start/ui";
import { BadgeCheck, CalendarClock, RotateCcw } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../../app/use-app-context.js";
import {
  clearVerifications,
  completedMentorCompetencies,
  formatMentorWhen,
  mentorMeeting,
  mentorMentees,
  verifyCompetency,
} from "../data/mentor-mentees.js";

export function MentorMenteesPage() {
  const { t, i18n } = useTranslation();
  const { persona } = useAppContext();
  const [, setRevision] = useState(0);
  const mentees = mentorMentees.map((teacher) => ({
    ...teacher,
    completed: completedMentorCompetencies(teacher.id),
    nextMeeting: mentorMeeting(teacher.id),
  }));
  const verifiedCount = mentees
    .flatMap((teacher) => teacher.completed)
    .filter((skill) => skill.verified).length;
  if (persona.kind === "employee") return null;

  return (
    <>
      <PageHeading
        action={
          <Button
            controlId="mentor.mentees.reset"
            disabled={verifiedCount === 0}
            onClick={() => {
              clearVerifications();
              setRevision((current) => current + 1);
            }}
            type="button"
            variant="secondary"
          >
            <RotateCcw aria-hidden="true" className="size-4" />
            {t("mentorOverview.reset")}
          </Button>
        }
        subtitle={t("mentorOverview.subtitle")}
        title={t("mentorOverview.title")}
      />
      <p className="mb-5 text-sm text-muted-foreground">
        {t("mentorOverview.teacherCount", { count: mentees.length })}
      </p>
      <div className="grid gap-4">
        {mentees.map((teacher) => (
          <Card aria-labelledby={`mentor-teacher-${teacher.id}`} key={teacher.id}>
            <h2 className="text-lg font-medium" id={`mentor-teacher-${teacher.id}`}>
              {teacher.name}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">{teacher.context}</p>
            {teacher.nextMeeting ? (
              <p
                className="mt-4 flex items-start gap-2 text-sm font-medium"
                data-control-id={`mentor.mentees.meeting.${teacher.id}`}
              >
                <CalendarClock aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
                <span>
                  {t("mentorOverview.nextMeeting", {
                    when: formatMentorWhen(teacher.nextMeeting, i18n.language),
                  })}
                </span>
              </p>
            ) : null}
            <h3 className="mt-5 text-sm font-medium">{t("mentorOverview.completedHeading")}</h3>
            {teacher.completed.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">{t("mentorOverview.noneCompleted")}</p>
            ) : (
              <ul className="mt-3 divide-y divide-border">
                {teacher.completed.map((competency) => {
                  return (
                    <li
                      className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                      key={competency.id}
                    >
                      <div>
                        <p className="font-medium">{t(`startingTeacher.skills.${competency.id}.title`)}</p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {t("mentorOverview.completedOn", {
                            date: formatMentorWhen(competency.completedOn, i18n.language),
                          })}
                        </p>
                      </div>
                      {competency.verified ? (
                        <p
                          className="inline-flex min-h-10 items-center gap-2 text-sm font-medium"
                          data-control-id={`mentor.mentees.verified.${teacher.id}.${competency.id}`}
                        >
                          <BadgeCheck aria-hidden="true" className="size-4" />
                          {t("mentorOverview.verified")}
                        </p>
                      ) : (
                        <Button
                          controlId={`mentor.mentees.verify.${teacher.id}.${competency.id}`}
                          onClick={() => {
                            verifyCompetency(teacher.id, competency.id);
                            setRevision((current) => current + 1);
                          }}
                        >
                          {t("mentorOverview.verify")}
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        ))}
      </div>
    </>
  );
}
